import React, { useState } from "react";
import { KuikaInputAutoSize } from "../../../../ui-kit/kuika-input/kuika-input";
import KuikaMentions, { IKuikaMentionOption } from "../../../../ui-kit/kuika-mentions/kuika-mentions";
import KuikaSmallText from "../../../../ui-kit/typography/SmallText/SmallText";
import { ShowcaseFrame } from "../_layout/showcase-frame";
import { SpecRow } from "../_layout/spec-row";

// A workflow's parameters plus the one built-in, the list the workflow step description offers.
const PARAMETERS: IKuikaMentionOption[] = [
  { value: "customerId", label: "customerId" },
  { value: "customerName", label: "customerName" },
  { value: "orderTotal", label: "orderTotal" },
  { value: "approvalLevel", label: "approvalLevel" },
  { value: "processInitiator", label: "Process Initiator" }
];

const WITH_DISABLED: IKuikaMentionOption[] = [
  { value: "customerId", label: "customerId" },
  { value: "legacyCode", label: "legacyCode — retired", disabled: true },
  { value: "orderTotal", label: "orderTotal" }
];

const TAGS: IKuikaMentionOption[] = [
  { value: "urgent", label: "urgent" },
  { value: "finance", label: "finance" },
  { value: "review", label: "review" }
];

const AUTO_SIZE: KuikaInputAutoSize = { minRows: 2, maxRows: 6 };

// Uncontrolled and saved on blur, the way the workflow step description persists: a pick by
// pointer keeps the caret in the field, so nothing is saved until the reader leaves it.
const SaveOnBlurDemo: React.FC = (): JSX.Element => {
  const [saved, setSaved] = useState<string>("");
  const handleBlur = (event: React.FocusEvent<HTMLTextAreaElement>): void => {
    setSaved(event.target.value);
  };
  return (
    <div className="kuika-flex-column--gap-s kuika-width-full">
      <KuikaMentions
        options={PARAMETERS}
        defaultValue="Approve the order of @customerName "
        placeholder="You can use process parameters in description simply by starting with '@'"
        autoSize={AUTO_SIZE}
        onBlur={handleBlur}
        dataTestId="mentionsSaveOnBlurDemo"
      />
      <KuikaSmallText>{saved === "" ? "Nothing saved yet" : `Saved on blur: ${saved}`}</KuikaSmallText>
    </div>
  );
};

// Controlled: the parent owns the text and sees every edit, a pick included.
const ControlledDemo: React.FC = (): JSX.Element => {
  const [text, setText] = useState<string>("");
  const [lastPick, setLastPick] = useState<string>("none");
  const handleSelect = (option: IKuikaMentionOption, prefix: string): void => {
    setLastPick(`${prefix}${option.value}`);
  };
  return (
    <div className="kuika-flex-column--gap-s kuika-width-full">
      <KuikaMentions
        options={PARAMETERS}
        value={text}
        onChange={setText}
        onSelect={handleSelect}
        autoSize={AUTO_SIZE}
        notFoundContent="No parameter matches"
        placeholder="Type @ to insert a parameter"
      />
      <KuikaSmallText>{`${text.length} characters · last pick: ${lastPick}`}</KuikaSmallText>
    </div>
  );
};

export const MentionsStory: React.FC = (): JSX.Element => {
  return (
    <ShowcaseFrame
      title="Mentions"
      componentName="KuikaMentions"
      sourcePath="ui-kit/kuika-mentions"
      description="A textarea that offers a list when the reader types a prefix (@ by default) and writes prefix + value + a space in place of what was typed. The list opens at the mention, flips above it when there is no room below, follows ↑/↓ without wrapping, picks on Enter, Tab or a click without taking focus from the field, and Escape closes the list rather than the drawer or modal around it. The text stays plain."
    >
      <SpecRow label="Save on blur" caption="uncontrolled — the workflow step description's shape" align="stretch">
        <SaveOnBlurDemo />
      </SpecRow>
      <SpecRow label="Controlled" caption="with notFoundContent — try @zzz" align="stretch">
        <ControlledDemo />
      </SpecRow>
      <SpecRow label="Disabled option" caption="↑/↓ step over it; it cannot be picked" align="stretch">
        <div className="kuika-width-full">
          <KuikaMentions options={WITH_DISABLED} placeholder="Type @ and move with the arrow keys" />
        </div>
      </SpecRow>
      <SpecRow label="Custom prefix" caption={'prefix="#"'} align="stretch">
        <div className="kuika-width-full">
          <KuikaMentions options={TAGS} prefix="#" placeholder="Type # to add a tag" />
        </div>
      </SpecRow>
      <SpecRow label="Disabled" align="stretch">
        <div className="kuika-width-full">
          <KuikaMentions options={PARAMETERS} defaultValue="Notify @processInitiator " disabled />
        </div>
      </SpecRow>
    </ShowcaseFrame>
  );
};
